<script>
  import Breadcrumbs from './Breadcrumbs.svelte';
  import { notes } from './data/notes.js';

  let { selectNote = () => {}, navigate = () => {} } = $props();

  let searchQuery = $state('');

  // Filter notes using Svelte 5 $derived
  let filteredNotes = $derived(
    notes.filter(note => {
      const query = searchQuery.toLowerCase().trim();
      return !query || 
        note.title.toLowerCase().includes(query) ||
        note.summary.toLowerCase().includes(query);
    })
  );
</script>

<div class="notes-page-container">
  <div class="header-banner panel">
    <div class="top-row">
      <!-- CS DIRECTORY FILE PATH NAVIGATION -->
      <Breadcrumbs 
        currentView="notes" 
        onnavigate={(view, slug) => navigate(view, slug)} 
      />
      <span class="note-count">{notes.length} {notes.length === 1 ? 'Note' : 'Notes'}</span>
    </div>
    
    <h1>Notes & Thoughts</h1>
    <p class="subtitle">A digital notebook for code snippets, physics ramblings, anime, and random queries.</p>

    <div class="controls-row">
      <div class="search-box">
        <input 
          type="text" 
          bind:value={searchQuery} 
          placeholder="Search notes..." 
          class="theme-input search-input"
        />
        {#if searchQuery}
          <button class="clear-search" onclick={() => searchQuery = ''}>✕</button>
        {/if}
      </div>
    </div>
  </div>

  <div class="notes-grid">
    {#if filteredNotes.length === 0}
      <div class="panel empty-panel">
        <p class="empty-text">No notes found matching "{searchQuery}".</p>
        <button class="theme-button reset-btn" onclick={() => searchQuery = ''}>
          Reset Search
        </button>
      </div>
    {:else}
      {#each filteredNotes as note (note.id)}
        <div 
          class="panel note-card" 
          role="button" 
          tabindex="0" 
          onclick={() => selectNote(note.id)}
          onkeydown={(e) => (e.key === 'Enter' || e.key === ' ') && selectNote(note.id)}
        >
          <div class="card-meta">
            <span class="date">{note.date}</span>
            <span class="dot">•</span>
            <span class="read-time">{note.readTime}</span>
          </div>

          <h2 class="card-title">
            <a href="#/notes/{note.id}" onclick={(e) => { e.preventDefault(); selectNote(note.id); }}>
              {note.title}
            </a>
          </h2>

          <p class="card-summary">{note.summary}</p>

          <div class="card-footer">
            <span class="read-more">Read Note →</span>
          </div>
        </div>
      {/each}
    {/if}
  </div>
</div>

<style>
  .notes-page-container {
    display: flex;
    flex-direction: column;
    gap: 1.5rem;
    width: 100%;
    max-width: 1200px;
    margin: 0 auto;
    animation: fade 0.3s ease-in-out;
  }

  @keyframes fade {
    from { opacity: 0; transform: translateY(6px); }
    to { opacity: 1; transform: translateY(0); }
  }

  .panel {
    background: #000000;
    border: 1px solid #ffffff;
    border-radius: clamp(16px, 2vw, 32px);
    padding: clamp(1.5rem, 3vw, 2.5rem);
  }

  .header-banner {
    display: flex;
    flex-direction: column;
    gap: 1rem;
  }

  .top-row {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 1rem;
    flex-wrap: wrap;
  }

  .note-count {
    font-size: 0.85rem;
    color: #888;
    letter-spacing: 0.5px;
    white-space: nowrap;
  }

  h1 {
    font-size: clamp(1.8rem, 3vw, 2.4rem);
    margin: 0;
    font-weight: 700;
    color: #ffffff;
  }

  .subtitle {
    margin: 0;
    color: #bbb;
    font-size: clamp(0.95rem, 1.2vw, 1.05rem);
  }

  .controls-row {
    display: flex;
    margin-top: 0.5rem;
  }

  .search-box {
    position: relative;
    width: 100%;
    max-width: 450px;
  }

  .theme-input {
    width: 100%;
    background: #121212;
    border: 1px solid #333;
    color: #e0e0e0;
    padding: 0.7rem 1rem;
    border-radius: 12px;
    font-family: inherit;
    font-size: 0.95rem;
    box-sizing: border-box;
    transition: border-color 0.2s;
  }

  .theme-input:focus {
    outline: none;
    border-color: #ffffff;
  }

  .clear-search {
    position: absolute;
    right: 12px;
    top: 50%;
    transform: translateY(-50%);
    background: transparent;
    border: none;
    color: #888;
    cursor: pointer;
    font-size: 1rem;
  }

  .clear-search:hover {
    color: #fff;
  }

  .notes-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
    gap: 1.5rem;
  }

  .note-card {
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    cursor: pointer;
    background: #000000;
    transition: transform 0.2s ease, border-color 0.2s ease;
  }

  .note-card:hover {
    transform: translateY(-3px);
    border-color: #ffffff;
  }

  .card-meta {
    font-size: 0.85rem;
    color: #888;
    margin-bottom: 0.5rem;
    display: flex;
    align-items: center;
    gap: 0.4rem;
  }

  .card-title {
    margin: 0 0 0.75rem 0;
    font-size: 1.3rem;
    font-weight: 600;
    line-height: 1.4;
  }

  .card-title a {
    color: #ffffff;
    text-decoration: none;
    transition: color 0.15s;
  }

  .card-title a:hover {
    text-decoration: underline;
  }

  .card-summary {
    font-size: 0.95rem;
    color: #bbb;
    line-height: 1.6;
    margin: 0 0 1.5rem 0;
  }

  .card-footer {
    display: flex;
    justify-content: flex-end;
    align-items: center;
    margin-top: auto;
    padding-top: 1rem;
    border-top: 1px solid #1a1a1a;
  }

  .read-more {
    font-size: 0.85rem;
    color: #ffffff;
    font-weight: 600;
    display: flex;
    align-items: center;
    gap: 0.2rem;
  }

  .empty-panel {
    grid-column: 1 / -1;
    text-align: center;
    padding: 3rem 1.5rem;
  }

  .empty-text {
    color: #888;
    font-size: 1.1rem;
    margin-bottom: 1rem;
  }

  .theme-button {
    background: #e0e0e0;
    color: #000;
    font-weight: 600;
    border: none;
    padding: 0.6rem 1.2rem;
    border-radius: 8px;
    cursor: pointer;
  }

  .theme-button:hover {
    opacity: 0.85;
  }
</style>
